/**
 * Sender 附件上传规则（格式、大小、数量）。
 * 后期变更时只需改本文件。
 */

export const SENDER_MAX_FILES = 5;
/** 图片单文件大小上限（MB） */
export const SENDER_MAX_IMAGE_SIZE_MB = 10;
/** 文档单文件大小上限（MB） */
export const SENDER_MAX_FILE_SIZE_MB = 25;

const MB_IN_BYTES = 1024 * 1024;

export const SENDER_IMAGE_ATTACHMENT_RULE = {
  /** 用户可见格式列表（与产品表一致） */
  displayFormats: "JPG, JPEG, PNG, HEIC, WebP",
  /** 校验用扩展名（小写，不含点）；heif 为 HEIC 常见别名 */
  extensions: ["jpg", "jpeg", "png", "heic", "heif", "webp"] as const,
} as const;

export const SENDER_DOCUMENT_ATTACHMENT_RULE = {
  displayFormats: "PDF, DOCX, XLSX, CSV, TXT",
  extensions: ["pdf", "docx", "xlsx", "csv", "txt"] as const,
} as const;

const IMAGE_EXTENSION_SET = new Set<string>(
  SENDER_IMAGE_ATTACHMENT_RULE.extensions,
);
const DOCUMENT_EXTENSION_SET = new Set<string>(
  SENDER_DOCUMENT_ATTACHMENT_RULE.extensions,
);

export type SenderAttachmentFileKind = "image" | "document" | "unsupported";

export function getFileExtension(filename: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) {
    return "";
  }
  return filename.slice(dot + 1).toLowerCase();
}

export function getSenderAttachmentFileKind(
  filename: string,
): SenderAttachmentFileKind {
  const ext = getFileExtension(filename);
  if (IMAGE_EXTENSION_SET.has(ext)) {
    return "image";
  }
  if (DOCUMENT_EXTENSION_SET.has(ext)) {
    return "document";
  }
  return "unsupported";
}

function getMaxSizeBytesForKind(
  kind: Exclude<SenderAttachmentFileKind, "unsupported">,
): number {
  const mb =
    kind === "image" ? SENDER_MAX_IMAGE_SIZE_MB : SENDER_MAX_FILE_SIZE_MB;
  return mb * MB_IN_BYTES;
}

export function isSenderAttachmentFileOversized(file: File): boolean {
  const kind = getSenderAttachmentFileKind(file.name);
  if (kind === "unsupported") {
    return false;
  }
  return file.size > getMaxSizeBytesForKind(kind);
}
